import { isDefined } from "@clipboard-health/util-ts";
import { convertToGeoLocation } from "@src/appV2/Location";
import { useDrivingDistanceToWorkplace } from "@src/appV2/redesign/Workplace/useDrivingDistanceToWorkplace";
import { secondsToMinutes } from "date-fns";

import type { ArrivalContext } from "../types";

interface UseTravelTimeParams {
  arrivalContext: ArrivalContext;
  facilityCoordinates?: [number, number];
}

/**
 * Single-responsibility hook for calculating travel time
 * Only calculates when we have a travel location and facility coordinates
 */
export function useTravelTime({ arrivalContext, facilityCoordinates }: UseTravelTimeParams) {
  const facilityGeoLocation = isDefined(facilityCoordinates)
    ? convertToGeoLocation(facilityCoordinates)
    : undefined;

  const travelFromGeoLocation = arrivalContext.travelFromLocation
    ? {
        latitude: arrivalContext.travelFromLocation.lat,
        longitude: arrivalContext.travelFromLocation.lng,
      }
    : undefined;

  const shouldCalculateDistance =
    isDefined(facilityGeoLocation) &&
    isDefined(travelFromGeoLocation) &&
    (arrivalContext.state === "traveling" || arrivalContext.state === "ready-to-travel");

  const { data: distanceData, isLoading } = useDrivingDistanceToWorkplace({
    workplaceGeoLocation: facilityGeoLocation,
    workerGeoLocation: travelFromGeoLocation,
    enabled: shouldCalculateDistance,
  });

  const estimatedTravelTime = isDefined(distanceData?.duration?.value)
    ? Math.ceil(secondsToMinutes(distanceData.duration.value))
    : undefined;

  return {
    estimatedTravelTime,
    isLoading,
    travelFromType: arrivalContext.travelFromLocation?.type,
  };
}
